/**
 * The firm-by-firm comparison (DEC-22): BTC and BTI by firm, critical controls by firm, areas, and the table. One set
 * of parts for the National view (National IT) and Leadership's "The network" tab (National IT and the Board), drawn
 * from the same API rows (firmComparison in packages/shared), so a firm reads the same on both pages.
 *
 * In place counts fully or partly, drawn apart. A firm's own 2026 rating is marked "(self)", March 2025 survey answers
 * "(2025)"; neither enters a median, and the median tick is the CDW firms' only.
 */
import { areaName, BASELINES, DELIVERY_STATUS, standingOf, type FirmComparison, type FirmComparisonRow, type ComparisonSurveyFigure } from '@grc/shared/vocabulary';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { fullyText, Heatmap, RankedBars } from '../charts';
import { AnswerSection, formatDate, Status } from '../components';
import { useFrom } from '../origin';

const pct = (v: number | null) => (v === null ? 'No results' : `${Math.round(v)}%`);
const PLAN_ORDER = ['off_track', 'at_risk', 'on_track', 'not_yet_committed', 'complete'];

/**
 * A card for one comparison part: h2 on the National view, h3 inside Leadership's network band. On Leadership (level 3)
 * it takes the structured form of the tab's other sections (F1, NEXT PASS 1): the part's name, its question as the
 * heading, the answer with its chip, one line saying what it means, and its source (the shared AnswerSection).
 */
function Part({ id, title, level, source, question, answer, chip, meaning, children }: {
  id: string;
  title: string;
  level: 2 | 3;
  /** 5D (F2, Leadership only): where the figures come from, said under the part. */
  source?: string;
  question?: string;
  answer?: string;
  chip?: { label: string; tone: string };
  meaning?: ReactNode;
  children: ReactNode;
}) {
  if (level === 3 && question) {
    return (
      <AnswerSection id={id} name={title} question={question} answer={answer} chip={chip} meaning={meaning} source={source} className="compare-part">
        {children}
      </AnswerSection>
    );
  }
  const H = level === 2 ? 'h2' : 'h3';
  return (
    <section className="card lead-wide compare-part" aria-labelledby={id}>
      <H id={id}>{title}</H>
      {meaning && <p className="muted small">{meaning}</p>}
      {children}
      {source && <p className="lead-source">Source: {source}</p>}
    </section>
  );
}

/** The spread of a chart's values, as the structured form's answer: "35% to 100%". */
const spread = (values: Array<number | null>): string => {
  const v = values.filter((x): x is number => x !== null);
  return v.length ? `${pct(Math.min(...v))} to ${pct(Math.max(...v))}` : 'No results yet';
};
/** The typical firm's figure as the chip. */
const typicalChip = (median: number | null) => ({ label: median === null ? 'No typical firm yet' : `Typical firm ${pct(median)}`, tone: 'neutral' });

/**
 * A firm's bar: its 2026 figure with the partly share, or for a firm with no 2026 rating its March 2025 survey answers.
 * BTC goes through standingOf, the rule Leadership's "Where firms stand" reads too, so the two charts agree (N2).
 */
const baselineBar = (f: FirmComparisonRow, which: 'btc' | 'bti') => {
  if (which === 'btc') {
    const s = standingOf(f);
    return { key: f.firmCode, label: f.firmName, value: s.pct, partly: s.partlyPct, marked: s.source === 'self' ? true : s.source === 'survey' ? ('survey' as const) : false };
  }
  return {
    key: f.firmCode,
    label: f.firmName,
    value: f.source === null ? (f.march2025?.bti.pct ?? null) : f.btiPct,
    // The March 2025 survey only knew achieved or not, so it has no split.
    partly: f.source === null ? null : (f.btiPartlyPct ?? null),
    marked: f.source === 'self' ? true : f.source === null ? ('survey' as const) : false,
  };
};

/** The BTC-by-firm chart's one name, on the National view and Leadership's "The network" (N4). */
export const BTC_CHART = 'BTC Baseline by firm';

/** The BTC Baseline's one name on both pages: the short name DEC-24 set for Leadership, which shares these parts. */
export const BTC_NAME = 'BTC Baseline';

/** Firms shown in a baseline chart: with a 2026 rating, or with March 2025 survey answers. */
const inBaselines = (rows: FirmComparisonRow[]) => rows.filter((f) => f.source !== null || f.march2025);

/** BTC Baseline or BTI Baseline by firm, highest first, with the CDW firms' median as a tick. */
export function BaselineByFirm({ c, which, level, you, source }: { c: FirmComparison; which: 'btc' | 'bti'; level: 2 | 3; you?: string | null; source?: string }) {
  const b = BASELINES[which];
  const rows = inBaselines(c.rows).map((f) => ({ ...baselineBar(f, which), you: f.firmCode === you }));
  const median = which === 'btc' ? c.medians.btc : c.medians.bti;
  return (
    <Part
      id={`compare-${which}-h`}
      title={which === 'btc' ? BTC_CHART : `${b.name} by firm`}
      level={level}
      source={source}
      question={`How much of the ${which === 'btc' ? BTC_NAME : b.name} does each firm have in place?`}
      answer={spread(rows.map((x) => x.value))}
      chip={typicalChip(median)}
      meaning={<>Share of the {which === 'btc' ? '56 BTC Baseline controls' : '39 BTI requirements (Waves 1 and 2)'} in place, fully or partly; the tick is the median of firms with CDW results.</>}
    >
      <RankedBars caption={which === 'btc' ? BTC_NAME : b.name} median={median} rows={rows} />
    </Part>
  );
}

/** Critical protections in place, firm by firm. */
export function CriticalByFirm({ c, level, you, source }: { c: FirmComparison; level: 2 | 3; you?: string | null; source?: string }) {
  const critical = criticalRows(c, you);
  return (
    <Part
      id="compare-critical-h"
      title="Critical protections in place, by firm"
      level={level}
      source={source}
      question="How many of the critical protections does each firm have in place?"
      answer={spread(critical.map((x) => x.value))}
      chip={typicalChip(c.medians.criticalPct)}
      meaning={<>Share of the controls with Critical priority (those that stop the most common attacks, including ransomware) in place, fully or partly. From the CDW assessment, National IT&rsquo;s accepted review, or the firm&rsquo;s own rating while CDW has not assessed it. The tick is the median of firms with CDW results.</>}
    >
      <RankedBars
        caption="Critical protections in place"
        median={c.medians.criticalPct}
        rows={critical}
      />
    </Part>
  );
}

/** Each firm's critical protections in place, for the chart and its answer. */
function criticalRows(c: FirmComparison, you?: string | null) {
  return c.rows
          .filter((f) => f.source !== null)
          .map((f) => ({
            key: f.firmCode,
            label: f.firmName,
            value: f.criticalRated ? Math.round(((f.criticalRated - (f.criticalOpen ?? 0)) / f.criticalRated) * 1000) / 10 : null,
            partly: f.criticalRated && f.criticalPartly !== null ? Math.round((f.criticalPartly / f.criticalRated) * 1000) / 10 : null,
            marked: f.source === 'self',
            you: f.firmCode === you,
          }));
}

/** Each area's share in place, firm by firm (firms with CDW results), ordered by BTC Baseline. */
export function AreasByFirm({ c, level, source }: { c: FirmComparison; level: 2 | 3; source?: string }) {
  const areas = c.domains.map((d) => ({ key: d.domainNumber, label: areaName(d.domainNumber, d.domain) }));
  const firms = c.rows
    .filter((f) => f.hasResults)
    .sort((a, b) => (b.btcPct ?? -1) - (a.btcPct ?? -1))
    .map((f) => ({ key: f.firmCode, label: f.firmName, values: f.areas, partly: f.areasPartly }));
  // The area with the least in place at the typical firm (median across firms), as the structured form's answer.
  const medians = areas.map((a) => {
    const v = firms.map((f) => f.values[a.key]).filter((x): x is number => typeof x === 'number').sort((x, y) => x - y);
    return { label: a.label, median: v.length ? (v.length % 2 ? v[(v.length - 1) / 2]! : (v[v.length / 2 - 1]! + v[v.length / 2]!) / 2) : null };
  });
  const least = medians.filter((m) => m.median !== null).sort((x, y) => x.median! - y.median!)[0];
  return (
    <Part
      id="compare-areas-h"
      title="By area"
      level={level}
      source={source}
      question="Which area has the least in place at the typical firm?"
      answer={least ? least.label : 'No results yet'}
      chip={least ? { label: `Typical firm ${pct(least.median)}`, tone: 'neutral' } : undefined}
      meaning={<>Share of each area&rsquo;s controls in place, fully or partly, at firms with CDW results, ordered by {BTC_NAME}. Self-assessed firms are in the charts above and the table, not here.</>}
    >
      <Heatmap caption="Share of controls in place, by firm and area" firms={firms} areas={areas} />
    </Part>
  );
}

/**
 * A baseline cell for a firm with no 2026 rating yet: its March 2025 survey figure, marked as such. One element holds
 * the whole value (DEC-62), so a phone's firm card sets it against the right edge in one piece, beside its label.
 */
function SurveyCell({ figure }: { figure: ComparisonSurveyFigure }) {
  return (
    <span className="cell-value">
      {pct(figure.pct)}{' '}
      <span className="muted small">
        (March 2025 survey, {figure.inPlace} of {figure.surveyed} surveyed)
      </span>
    </span>
  );
}

/** A baseline cell: in place (fully or partly), and how much of it fully, in the charts' form: "100% (64% fully)". */
function BaselineCell({ pct: p, partlyPct }: { pct: number | null; partlyPct: number | null }) {
  // As the charts do: the fully share only when part of it is only partly in place.
  const fully = p !== null && partlyPct ? fullyText(p, partlyPct) : '';
  // One element for the whole value, as in SurveyCell (DEC-62).
  return (
    <span className="cell-value">
      {pct(p)}
      {fully && <span className="muted small"> ({fully})</span>}
    </span>
  );
}

/** What the table counts when it shows where firms are today only (Leadership, 5D). */
const TODAY_MEASURE =
  "Where each firm is today: controls in place (fully or partly) from its CDW assessment, or from National IT's accepted review when that is newer; maturity and higher risks from the same assessment. A firm waiting for CDW shows its own 2026 rating, and a firm with no 2026 rating yet its March 2025 survey answers; both are marked, and neither counts in a network median.";

/**
 * Every firm in one table: baselines, maturity, critical controls, higher risks, plans, March 2025, last update.
 * `today` (Leadership's network tab, 5D, DEC-49): where firms are today only, without the action plans, March 2025
 * survey and last update columns, which stay on the National view.
 */
export function FirmByFirmTable({ c, level, today = false, source }: { c: FirmComparison; level: 2 | 3; today?: boolean; source?: string }) {
  // A firm opened from here comes back here, Leadership's tab and picked firm included (CP-04).
  const from = useFrom();
  return (
    <Part
      id="compare-table-h"
      title="Firm by firm"
      level={level}
      source={source}
      question="Where is each firm today?"
      answer={`${c.rows.length} firms`}
      chip={{ label: `${c.rows.filter((f) => f.hasResults).length} with CDW results`, tone: 'neutral' }}
      meaning={today ? TODAY_MEASURE : c.measure}
    >
      {/* Wide on a phone: scrolls sideways, and can be reached and scrolled by keyboard. */}
      <div className="table-wrap" tabIndex={0} role="region" aria-label="Firm by firm table">
        {/* 5E NEXT PASS 5 (E1): on a phone each firm becomes a card, its figures labelled (data-label, app.css). */}
        <table className="compare stack-phone">
          <caption className="sr-only">{today ? 'Every firm’s results and risks today' : 'Every firm’s results, risks and action plans'}</caption>
          <thead>
            <tr>
              <th scope="col">Firm</th>
              <th scope="col">{BTC_NAME}</th>
              <th scope="col">{BASELINES.bti.name}</th>
              <th scope="col">Maturity (0 to 5)</th>
              <th scope="col">Critical protections not yet in place</th>
              <th scope="col">Higher risks open</th>
              {!today && (
                <>
                  <th scope="col">Action plans</th>
                  <th scope="col">March 2025 survey (BTC Baseline controls reported in place)</th>
                  <th scope="col">Last update</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {[...c.rows]
              .sort((a, b) => (b.btcPct ?? -1) - (a.btcPct ?? -1) || a.firmName.localeCompare(b.firmName))
              .map((f) => (
                <tr key={f.firmCode}>
                  <th scope="row">
                    <Link to={`/firm/${f.firmCode}`} state={from}>{f.firmName}</Link>
                    {f.source === 'self' && <div className="muted small">Self-assessed, {f.selfRated} of {f.controls} rated</div>}
                    {f.source === null && f.march2025 && <div className="muted small">No 2026 rating yet; baselines from its March 2025 survey</div>}
                  </th>
                  <td data-label={BTC_NAME}>{f.source === null && f.march2025 ? <SurveyCell figure={f.march2025.btc} /> : <BaselineCell pct={f.btcPct} partlyPct={f.btcPartlyPct} />}</td>
                  <td data-label={BASELINES.bti.name}>{f.source === null && f.march2025 ? <SurveyCell figure={f.march2025.bti} /> : <BaselineCell pct={f.btiPct} partlyPct={f.btiPartlyPct} />}</td>
                  <td data-label="Maturity (0 to 5)">{f.maturity === null ? 'Not rated' : f.maturity.toFixed(1)}</td>
                  <td data-label="Critical protections not yet in place">{f.criticalOpen === null ? 'No results' : `${f.criticalOpen} of ${f.criticalRated}`}</td>
                  <td data-label="Higher risks open">{f.highRisks === null ? 'No results' : f.highRisks}</td>
                  {!today && (<>
                  <td data-label="Action plans">
                    {Object.keys(f.actionPlans).length ? (
                      <span className="plans">
                        {PLAN_ORDER.filter((k) => f.actionPlans[k]).map((k) => (
                          <span key={k}>
                            {f.actionPlans[k]} <Status map={DELIVERY_STATUS} value={k} />
                          </span>
                        ))}
                      </span>
                    ) : (
                      'None recorded'
                    )}
                  </td>
                  <td data-label="March 2025 survey">{f.survey2025Pct === null ? 'No response' : `${Math.round(f.survey2025Pct)}%`}</td>
                  <td data-label="Last update">{f.lastUpdated ? formatDate(f.lastUpdated) : 'No updates yet'}</td>
                  </>)}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </Part>
  );
}
